import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, X, Loader2, Wrench, Trash2, Building2, ShieldCheck, CalendarClock, FolderOpen, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, beDate, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { Card, SectionCard, ListRow, EmptyState, Skeleton, StatusBadge } from "@/components/studio/StudioUI";

const EMPTY_EQ = {
  name: "", category: "chaudiere_gaz", brand: "", model: "", serial: "", power_kw: "", location: "",
  install_date: "", warranty_until: "", maintenance_interval_months: 12, last_maintenance: "", notes: "",
};

const STATE = {
  en_retard: ["Entretien en retard", "bg-rose-50 text-rose-800 border-rose-300"],
  a_programmer: ["À programmer", "bg-amber-50 text-amber-800 border-amber-300"],
  a_jour: ["À jour", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  inconnu: ["Date inconnue", "bg-slate-100 text-[#5C6A87] border-[#E2E7F7]"],
};

export const MaintenancePill = ({ state, testId }) => {
  const [label, cls] = STATE[state] || STATE.inconnu;
  return (
    <span data-testid={testId} className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}>
      {label}
    </span>
  );
};

export default function StudioSite() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_EQ);
  const [saving, setSaving] = useState(false);

  const load = () =>
    studio.get(`/sites/${id}`).then(({ data: d }) => setData(d)).catch(() => navigate("/studio/batiments"));

  useEffect(() => {
    load();
    studio.get("/assets/options").then(({ data: d }) => setOptions(d)).catch(() => {});
  }, [id]);

  const pickCategory = (value) => {
    const cat = (options?.equipment_categories || []).find((c) => c.value === value);
    setForm({ ...form, category: value, maintenance_interval_months: cat ? cat.default_interval : 12 });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: eq } = await studio.post("/equipments", { ...form, site_id: id });
      toast.success(`${eq.name} ajouté au bâtiment`);
      setForm(EMPTY_EQ);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Ajout impossible"));
    } finally {
      setSaving(false);
    }
  };

  const removeSite = async () => {
    if (!window.confirm("Supprimer ce bâtiment et tous ses équipements ?")) return;
    try {
      await studio.delete(`/sites/${id}`);
      toast.success("Bâtiment supprimé");
      navigate("/studio/batiments");
    } catch (err) {
      toast.error(apiError(err, "Suppression impossible"));
    }
  };

  const site = data?.site;

  return (
    <StudioLayout
      breadcrumb="Bâtiments"
      title={site?.name || "Bâtiment"}
      subtitle={site ? [site.address, site.postal_code, site.city].filter(Boolean).join(" ") : ""}
      action={
        <Link to="/studio/batiments" data-testid="back-to-sites" className={`${btnGhost} hidden lg:inline-flex`}>
          <ArrowLeft className="h-4 w-4" /> Tous les bâtiments
        </Link>
      }
    >
      {!data ? (
        <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-3">
            <SectionCard
              testId="site-equipments"
              title="Équipements"
              hint="Chaque appareil garde son passeport et son historique"
              action={
                <button
                  type="button"
                  data-testid="add-equipment-btn"
                  onClick={() => setOpen((v) => !v)}
                  className="inline-flex min-h-[38px] shrink-0 items-center gap-1.5 rounded-xl bg-[#3A5CC8] shadow-[0_1px_2px_rgba(15,38,69,0.14)] px-3.5 text-xs font-bold text-white active:scale-95"
                >
                  {open ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  {open ? "Fermer" : "Ajouter"}
                </button>
              }
            >
              {open && (
                <form onSubmit={submit} data-testid="equipment-form" className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD]/60 p-3 sm:grid-cols-2">
                  <select
                    data-testid="eq-category"
                    value={form.category}
                    onChange={(e) => pickCategory(e.target.value)}
                    className={inputCls}
                  >
                    {(options?.equipment_categories || []).map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    data-testid="eq-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Nom (laissez vide = auto)"
                    className={inputCls}
                  />
                  <input data-testid="eq-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Marque" className={inputCls} />
                  <input data-testid="eq-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Modèle" className={inputCls} />
                  <input data-testid="eq-serial" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} placeholder="N° de série" className={inputCls} />
                  <input data-testid="eq-power" value={form.power_kw} onChange={(e) => setForm({ ...form, power_kw: e.target.value })} placeholder="Puissance (kW)" className={inputCls} />
                  <input data-testid="eq-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Emplacement (cave, toiture, cuisine…)" className={`${inputCls} sm:col-span-2`} />
                  <label className="text-xs font-semibold text-[#5C6A87]">
                    Date d'installation
                    <input type="date" data-testid="eq-install" value={form.install_date} onChange={(e) => setForm({ ...form, install_date: e.target.value })} className={`${inputCls} mt-1`} />
                  </label>
                  <label className="text-xs font-semibold text-[#5C6A87]">
                    Garantie jusqu'au
                    <input type="date" data-testid="eq-warranty" value={form.warranty_until} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} className={`${inputCls} mt-1`} />
                  </label>
                  <label className="text-xs font-semibold text-[#5C6A87]">
                    Dernier entretien
                    <input type="date" data-testid="eq-last" value={form.last_maintenance} onChange={(e) => setForm({ ...form, last_maintenance: e.target.value })} className={`${inputCls} mt-1`} />
                  </label>
                  <label className="text-xs font-semibold text-[#5C6A87]">
                    Entretien tous les (mois)
                    <input type="number" min="0" max="240" data-testid="eq-interval" value={form.maintenance_interval_months} onChange={(e) => setForm({ ...form, maintenance_interval_months: Number(e.target.value) })} className={`${inputCls} mt-1`} />
                  </label>
                  <div className="flex gap-2 sm:col-span-2">
                    <button type="submit" data-testid="eq-submit" disabled={saving} className={btnPrimary}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      Ajouter l'équipement
                    </button>
                  </div>
                </form>
              )}

              {data.equipments.length === 0 ? (
                <EmptyState
                  testId="equipments-empty"
                  icon={Wrench}
                  title="Aucun équipement"
                  hint="Ajoutez la chaudière, la PAC, la ventilation ou le boiler. Vous obtiendrez un QR code à coller sur l'appareil et un rappel d'entretien automatique."
                />
              ) : (
                <div className="space-y-2">
                  {data.equipments.map((e) => (
                    <Link
                      key={e.id}
                      to={`/studio/equipements/${e.id}`}
                      data-testid={`equipment-row-${e.id}`}
                      className="flex min-h-[64px] items-center gap-3 rounded-xl border border-[#E2E7F7] bg-white px-3 py-2.5 transition-colors hover:border-[#3A5CC8]/40 hover:bg-[#F6F8FD]"
                    >
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EEF1FC] text-[#3A5CC8]">
                        <Wrench className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-sm font-semibold text-[#0F2645]">{e.name}</span>
                          <MaintenancePill state={e.maintenance_state} />
                          {e.under_warranty && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                              <ShieldCheck className="h-2.5 w-2.5" /> Garantie
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-[#5C6A87]">
                          {[e.category_label, e.serial && `n° ${e.serial}`, e.location].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      <span className="shrink-0 text-right text-[11px] text-[#93A0BC]">
                        {e.next_maintenance ? beDate(e.next_maintenance) : "—"}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard testId="site-history" title="Historique du bâtiment" hint="Tous les entretiens et réparations">
              {data.history.length === 0 ? (
                <EmptyState testId="site-history-empty" icon={CalendarClock} title="Pas encore d'historique" hint="Chaque entretien enregistré sur un équipement apparaît ici." />
              ) : (
                <div className="space-y-2">
                  {data.history.map((h) => (
                    <div key={h.id} data-testid={`history-${h.id}`} className="flex min-h-[52px] items-center gap-3 rounded-xl border border-[#E2E7F7] px-3 py-2">
                      <span className="w-[74px] shrink-0 text-xs font-bold tabular-nums text-[#5C6A87]">{beDate(h.date)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-[#0F2645]">
                          {h.type_label} — {h.equipment_name}
                        </span>
                        {h.notes && <span className="block truncate text-xs text-[#5C6A87]">{h.notes}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          <div className="space-y-4 lg:col-span-2">
            <SectionCard testId="site-info" title="Fiche du bâtiment">
              <dl className="space-y-2.5 text-sm">
                {[
                  ["Type", site.building_type_label],
                  ["Classe PEB", site.peb_class ? `PEB ${site.peb_class}` : ""],
                  ["Année de construction", site.year_built],
                  ["Surface chauffée", site.heated_area ? `${site.heated_area} m²` : ""],
                  ["Client", data.client ? data.client.company || data.client.name : ""],
                  ["Téléphone", data.client?.phone],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-xs font-semibold uppercase tracking-wide text-[#93A0BC]">{k}</dt>
                    <dd className="text-right text-sm font-semibold text-[#0F2645]">{v}</dd>
                  </div>
                ))}
              </dl>
              {site.access_note && (
                <div className="mt-3 flex gap-2 rounded-xl bg-amber-50 p-3">
                  <KeyRound className="h-4 w-4 shrink-0 text-amber-700" />
                  <p className="text-xs leading-relaxed text-amber-900">{site.access_note}</p>
                </div>
              )}
              <button
                type="button"
                data-testid="delete-site"
                onClick={removeSite}
                className="mt-4 inline-flex min-h-[40px] items-center gap-2 text-xs font-semibold text-rose-600 hover:underline"
              >
                <Trash2 className="h-3.5 w-3.5" /> Supprimer le bâtiment
              </button>
            </SectionCard>

            <SectionCard
              testId="site-dossiers"
              title="Chantiers sur ce bâtiment"
              action={
                <button
                  type="button"
                  data-testid="site-new-dossier"
                  onClick={async () => {
                    try {
                      const { data: d } = await studio.post("/dossiers", {
                        title: `Intervention — ${site.name}`,
                        client_id: site.client_id || "",
                        client_name: data.client ? data.client.company || data.client.name : "",
                        client_phone: data.client?.phone || "",
                        client_email: data.client?.email || "",
                        site_address: site.address || "",
                        site_postal_code: site.postal_code || "",
                        site_city: site.city || "",
                        site_id: site.id,
                      });
                      navigate(`/studio/dossiers/${d.id}`);
                    } catch (err) {
                      toast.error(apiError(err, "Création impossible"));
                    }
                  }}
                  className="inline-flex min-h-[38px] shrink-0 items-center gap-1.5 rounded-xl border border-[#E2E7F7] px-3 text-xs font-bold text-[#3A5CC8] hover:border-[#3A5CC8]"
                >
                  <Plus className="h-3.5 w-3.5" /> Chantier
                </button>
              }
            >
              {data.dossiers.length === 0 ? (
                <EmptyState testId="site-dossiers-empty" icon={FolderOpen} title="Aucun chantier lié" hint="Les dossiers rattachés à ce bâtiment apparaîtront ici." />
              ) : (
                <div className="space-y-2">
                  {data.dossiers.map((d) => (
                    <ListRow
                      key={d.id}
                      to={`/studio/dossiers/${d.id}`}
                      testId={`site-dossier-${d.id}`}
                      bar="bg-[#3A5CC8]"
                      title={d.reference}
                      subtitle={d.title}
                      status={d.status}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
